package com.shadowings.gero.presentation.model

import com.shadowings.gero.data.repository.Hiero
import com.shadowings.gero.domain.model.Chapter
import com.shadowings.gero.domain.model.LessonNode
import com.shadowings.gero.domain.model.UserData

data class UiLessonNode(
    val lesson: LessonNode,
    val isLocked: Boolean,
    val earnedStars: Int,
    val isPerfect: Boolean
) {
    val id: String get() = lesson.id
}

data class UiLessonRow(
    val lessons: List<UiLessonNode>
)

data class LandingUiState(
    val userData: UserData = UserData(),
    val hieros: List<Hiero> = emptyList(),
    val words: List<Word> = emptyList(),
    val chapters: List<Chapter> = emptyList(),
    val lessonRowsPerChapter: Map<Int, List<UiLessonRow>> = emptyMap(),
    val isLoggedIn: Boolean = false,
)

data class Word(
    val id: String,
    private val meaning: String,
    val reading: String,
    val gardiner: String,
    private val note: String,
    val alt1: String?,
    val alt2: String?,
    private val articolo: String? = null,
    private val genitivo: String? = null,
    val showInCompendio: Boolean = true,
    private val articoloEn: String? = null,
    private val genitivoEn: String? = null,
    private val noteEn: String? = null,
    private val meaningEn: String? = null,
    val isPlural: Boolean = false,
    private val articoloDe: String? = null,
    private val genitivoDe: String? = null,
    private val noteDe: String? = null,
    private val meaningDe: String? = null,
    private val articoloEs: String? = null,
    private val genitivoEs: String? = null,
    private val noteEs: String? = null,
    private val meaningEs: String? = null,
    val isFeminine: Boolean = false,
    val baseWordId: String? = null,
    private val articoloFr: String? = null,
    private val genitivoFr: String? = null,
    private val noteFr: String? = null,
    private val meaningFr: String? = null,
    val isStative: Boolean = false,
    val itForcePresent: Boolean = false,
    val isHumanOrDeity: Boolean = false,
    val isAnimal: Boolean = false,
    // German n-declension (words.tsv is_weak_noun_de): -(e)n outside the
    // nominative singular - "der Soldat / den, dem, des Soldaten", and
    // adjectival nouns after a definite determiner ("der Adlige / den Adligen").
    val isWeakNounDe: Boolean = false,
    // Italian/French grammatical gender (words.tsv is_feminine_it/_fr), needed
    // where the article can't tell ("l'"). isFeminine is the Egyptian gender.
    val isFeminineIt: Boolean = false,
    val isFeminineFr: Boolean = false,
    // Verb (words.tsv es_animal_object_no_a): its object is food or goods, so an animal takes no
    // Spanish personal "a" ("come el pez", "asa la oca"); people still do ("ofrece al dios").
    val esAnimalObjectNoA: Boolean = false,
    // Adjective word order (words.tsv adj_order_es / adj_order_fr): blank = only after the noun,
    // "both" = after first and before accepted, "pre" = before first and after accepted.
    val adjOrderEs: String? = null,
    val adjOrderFr: String? = null,
    // Adjective's shortened form before a masculine singular noun (words.tsv apocope_it /
    // apocope_es): "buon padre", "buen hombre", "gran mujer".
    val apocopeIt: String? = null,
    val apocopeEs: String? = null,
    // Noun (words.tsv fr_post_adjectives): adjective ids that go after it first in French even
    // when adj_order_fr is "pre" ("l'homme bon", since "le bon homme" reads as "le bonhomme").
    val frPostAdjectives: Set<String> = emptySet(),
    // Noun (words.tsv it_possessive_no_article): an Italian relative that drops the article with a
    // singular possessive other than loro ("mio padre", "il loro padre").
    val itPossessiveNoArticle: Boolean = false,
    // Personal pronoun (words.tsv is_pronoun): IO, TU_M, ..., ESSI.
    val isPronoun: Boolean = false,
    // A god with a proper name (words.tsv is_deity): Horus, Osiride, Ptah... not the common noun DIO.
    val isDeity: Boolean = false,
    // words.tsv de_directional: verb moves its object onto/into the place, so
    // German Wechselpräpositionen (auf/in) take the accusative ("auf den Altar").
    val deDirectional: Boolean = false
) {
    /**
     * Returns the primary meaning in the appropriate language based on the locale.
     * Always takes the first meaning before '/' or ',', trimmed.
     */
    fun getMeaning(languageCode: String): String {
        val raw = when (languageCode) {
            "it" -> meaning
            "de" -> meaningDe ?: meaningEn ?: meaning
            "es" -> meaningEs ?: meaningEn ?: meaning
            "fr" -> meaningFr ?: meaningEn ?: meaning
            else -> meaningEn ?: meaning
        }
        return raw.split("/")[0].split(",")[0].trim()
    }

    /**
     * Returns the raw full meaning string in the appropriate language.
     */
    fun getRawMeaning(languageCode: String): String {
        return when (languageCode) {
            "it" -> meaning
            "de" -> meaningDe ?: meaningEn ?: meaning
            "es" -> meaningEs ?: meaningEn ?: meaning
            "fr" -> meaningFr ?: meaningEn ?: meaning
            else -> meaningEn ?: meaning
        }
    }

    /**
     * Returns the note in the appropriate language based on the locale.
     */
    fun getNote(languageCode: String): String {
        return when (languageCode) {
            "it" -> note
            "de" -> noteDe ?: noteEn ?: note
            "es" -> noteEs ?: noteEn ?: note
            "fr" -> noteFr ?: noteEn ?: note
            else -> noteEn ?: note
        }
    }

    /**
     * Returns the article in the appropriate language based on the locale.
     * @param languageCode the language code (e.g., "it", "en", "de", "es", "fr")
     * @return the article in the specified language
     */
    fun getArticolo(languageCode: String): String? {
        return when (languageCode) {
            "it" -> articolo
            "de" -> articoloDe ?: articoloEn
            "es" -> articoloEs ?: articoloEn
            "fr" -> articoloFr ?: articoloEn
            else -> articoloEn
        }
    }

    /**
     * Returns the genitive form in the appropriate language based on the locale.
     * @param languageCode the language code (e.g., "it", "en", "de", "es", "fr")
     * @return the genitive form in the specified language
     */
    fun getGenitivo(languageCode: String): String? {
        return when (languageCode) {
            "it" -> genitivo
            "de" -> genitivoDe ?: genitivoEn
            "es" -> genitivoEs ?: genitivoEn
            "fr" -> genitivoFr ?: genitivoEn
            else -> genitivoEn
        }
    }

    fun isSeen(seenWords: Set<String>): Boolean {
        return meaning in seenWords || meaningEn in seenWords || meaningDe in seenWords || meaningEs in seenWords || meaningFr in seenWords
    }
}

data class Verb(
    val chiaveWords: String,
    val formaBaseSenzaDet: String,
    val traslitterazione: String,
    val determinativo: String,
    val formaImperfettiva: String,
    val traslitterazioneImperfettiva: String,
    val classe: String,
    val formaInfinita: String = "",
    val traslitterazioneInfinita: String = ""
)
